/**
 * Message rendering helpers for conversation threads.
 *
 * AI SDK model messages carry `{role, content:[parts]}` where parts mix
 * `text`, `tool-call` and `tool-result`. Tool parts are rendered as readable
 * one-line summaries instead of dumping the raw JSON into the bubble
 * (bug: assistant turns that only contained tool-calls showed as JSON blobs).
 */

/** Compact one-line summary of an arbitrary value, truncated for bubbles. */
export function compactValue(value: unknown, max = 140): string {
  let out: string;
  if (typeof value === 'string') out = value;
  else {
    try {
      out = JSON.stringify(value ?? '');
    } catch {
      out = String(value);
    }
  }
  out = out.replace(/\s+/g, ' ').trim();
  return out.length > max ? out.slice(0, max - 1) + '…' : out;
}

/** Extract a plain-text + structured flag from a message content fragment. */
export function messageDisplay(content: unknown): { text: string; structured: boolean } {
  if (typeof content === 'string') return { text: content, structured: false };
  if (!content || typeof content !== 'object') return { text: JSON.stringify(content, null, 2), structured: true };
  const c = content as Record<string, unknown>;
  if (typeof c.text === 'string') return { text: c.text, structured: false };
  const nested = c.content;
  if (typeof nested === 'string' && nested.length > 0) return { text: nested, structured: false };
  if (Array.isArray(nested)) {
    const lines: string[] = [];
    for (const p of nested) {
      if (typeof p === 'string') {
        if (p) lines.push(p);
        continue;
      }
      if (!p || typeof p !== 'object') continue;
      const part = p as Record<string, unknown>;
      if (part.type === 'tool-call') {
        const name = typeof part.toolName === 'string' ? part.toolName : 'tool';
        lines.push(`🔧 ${name} ${compactValue(part.input)}`.trim());
      } else if (part.type === 'tool-result') {
        const out = part.output ?? part.result;
        const empty = out === undefined || out === null || out === '' || out === '""';
        lines.push(`↩ ${empty ? '(empty result)' : compactValue(out)}`);
      } else if (typeof part.text === 'string' && part.text) {
        lines.push(part.text);
      }
    }
    if (lines.length > 0) return { text: lines.join('\n'), structured: false };
  }
  return { text: JSON.stringify(content, null, 2), structured: true };
}
